import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Stack, Row, Col, Card } from 'react-bootstrap';
import { ReactFlowProvider } from 'react-flow-renderer';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';
import { useOutsideComponentClick } from '../../../../../../../hooks/useOutsideComponentClick';
import { usePositionsQuery } from '../../../../../__generated__/graphql';
import {
  EmployeePosition,
  PositionChangeData,
  PositionChangeComponentProps,
} from './types';
import { getSmartFilteredPositions } from './utils';
import { getSmartTreesForPositions } from '../utils/treeLogic';
import { FlowContent } from './FlowContent';
import { ControlsPanel } from './ControlsPanel';
import { DateControls } from './DateControls';
import { PositionSummary } from './PositionSummary';
import './PositionChangeComponent.css';

export const PositionChangeComponent: React.FC<
  PositionChangeComponentProps
> = ({ selectedEmployeeId, onChange, value }) => {
  const t = useNamespacedTranslation(
    'changeRequests.requestForm.positionChange',
  );

  const [selectedPositionId, setSelectedPositionId] = useState<string>(
    value?.newPositionId || '',
  );
  const [searchQuery, setSearchQuery] = useState(value?.searchQuery || '');
  const [displayStaffedPositions, setDisplayStaffedPositions] = useState(
    value?.displayStaffedPositions ?? false,
  );
  const [showFullHierarchy, setShowFullHierarchy] = useState(
    value?.showFullHierarchy || false,
  );
  const [effectiveDate, setEffectiveDate] = useState(
    value?.effectiveDate || '',
  );
  const [unknownEndDate, setUnknownEndDate] = useState(
    value?.unknownEndDate ?? true,
  );
  const [endDate, setEndDate] = useState(value?.endDate || '');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useOutsideComponentClick(searchContainerRef as any, () => {
    setShowSearchResults(false);
  });

  const {
    data: positionsData,
    loading,
    error,
  } = usePositionsQuery({
    variables: {
      filter: {
        activeOnly: true,
      },
    },
  });

  const isEmployeeMainPosition = useCallback(
    (position: EmployeePosition): boolean => {
      return (
        position.employment?.flair__Employee__c === selectedEmployeeId &&
        position.employment?.flair__Main__c === true
      );
    },
    [selectedEmployeeId],
  );

  const positions = useMemo(() => {
    if (!positionsData?.positions) return [];
    return positionsData.positions
      .filter((position) => position != null)
      .map((position) => ({
        ...position,
        isCurrentPosition: isEmployeeMainPosition(position),
      })) as EmployeePosition[];
  }, [positionsData, isEmployeeMainPosition]);

  const currentPosition = useMemo(() => {
    return (
      positions.find((position) => isEmployeeMainPosition(position)) || null
    );
  }, [positions, isEmployeeMainPosition]);

  const hasMainEmployment = useMemo(() => {
    return positions.some((position) => isEmployeeMainPosition(position));
  }, [positions, isEmployeeMainPosition]);

  const flowPositions = useMemo(() => {
    if (!positions.length || !currentPosition) return [];

    let basePositions: EmployeePosition[];

    if (showFullHierarchy) {
      basePositions = positions;
    } else {
      basePositions = getSmartTreesForPositions(
        positions,
        currentPosition.Id,
        selectedPositionId || undefined,
      );
    }

    if (displayStaffedPositions) {
      return basePositions;
    }

    return getSmartFilteredPositions(basePositions, currentPosition.Id);
  }, [
    positions,
    currentPosition,
    showFullHierarchy,
    displayStaffedPositions,
    selectedPositionId,
  ]);

  const searchPositions = useMemo(() => {
    if (!positions.length) return [];

    let filtered = positions.filter(
      (pos) => !pos.isCurrentPosition && !pos.flair__Staffed__c,
    );

    if (searchQuery) {
      filtered = filtered.filter(
        (pos) =>
          pos.Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          pos.flair__Title__c
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()),
      );
    }

    return filtered.slice(0, 10);
  }, [positions, searchQuery]);

  const createChangeData = useCallback(
    (overrides: Partial<PositionChangeData> = {}): PositionChangeData => {
      if (!currentPosition) throw new Error('No current position available');

      const selectedPosition =
        positions.find((p) => p.Id === selectedPositionId) || currentPosition;

      return {
        currentPositionId: currentPosition.Id,
        newPositionId: selectedPositionId,
        currentPositionName:
          currentPosition.flair__Title__c || currentPosition.Name,
        newPositionName:
          selectedPosition.flair__Title__c || selectedPosition.Name,
        searchQuery,
        displayStaffedPositions,
        showFullHierarchy,
        effectiveDate,
        unknownEndDate,
        endDate,
        ...overrides,
      };
    },
    [
      currentPosition,
      selectedPositionId,
      positions,
      searchQuery,
      displayStaffedPositions,
      showFullHierarchy,
      effectiveDate,
      unknownEndDate,
      endDate,
    ],
  );

  const handlePositionSelect = useCallback(
    (positionId: string) => {
      setSelectedPositionId(positionId);
      setSearchQuery('');
      const newSelectedPosition = positions.find((p) => p.Id === positionId);
      onChange(
        createChangeData({
          newPositionId: positionId,
          newPositionName:
            newSelectedPosition?.flair__Title__c || newSelectedPosition?.Name,
          searchQuery: '',
        }),
      );
    },
    [createChangeData, onChange, positions],
  );

  const handleEffectiveDateChange = useCallback(
    (newEffectiveDate: string | null) => {
      setEffectiveDate(newEffectiveDate || '');
      onChange(createChangeData({ effectiveDate: newEffectiveDate || '' }));
    },
    [createChangeData, onChange],
  );

  const handleEndDateChange = useCallback(
    (newEndDate: string | null) => {
      setEndDate(newEndDate || '');
      onChange(createChangeData({ endDate: newEndDate || '' }));
    },
    [createChangeData, onChange],
  );

  const handleUnknownEndDateChange = useCallback(
    (newUnknownEndDate: boolean) => {
      setUnknownEndDate(newUnknownEndDate);
      onChange(createChangeData({ unknownEndDate: newUnknownEndDate }));
    },
    [createChangeData, onChange],
  );

  useEffect(() => {
    if (value?.newPositionId) {
      setSelectedPositionId(value.newPositionId);
    } else if (currentPosition) {
      setSelectedPositionId(currentPosition.Id);
    }
  }, [value?.newPositionId, currentPosition]);

  useEffect(() => {
    if (value) {
      if (value.searchQuery !== undefined) setSearchQuery(value.searchQuery);
      if (value.displayStaffedPositions !== undefined)
        setDisplayStaffedPositions(value.displayStaffedPositions);
      if (value.showFullHierarchy !== undefined)
        setShowFullHierarchy(value.showFullHierarchy);
      if (value.effectiveDate !== undefined)
        setEffectiveDate(value.effectiveDate);
      if (value.unknownEndDate !== undefined)
        setUnknownEndDate(value.unknownEndDate);
      if (value.endDate !== undefined) setEndDate(value.endDate);
    }
  }, [value]);

  const selectedPosition =
    positions.find((p) => p.Id === selectedPositionId) || currentPosition;

  if (!loading && !hasMainEmployment) {
    return (
      <div className="alert alert-warning d-flex align-items-center">
        <div className="flex-grow-1">
          <p className="mb-0">{t('noMainEmploymentMessage')}</p>
        </div>
      </div>
    );
  }

  if (!currentPosition && !loading) {
    return (
      <div className="text-center py-4">
        <div className="text-muted">{t('noCurrentPosition')}</div>
      </div>
    );
  }

  if (loading || !currentPosition) {
    return (
      <div className="position-change-section">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loadingPositions')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="position-change-section">
        <div className="alert alert-danger">
          {t('errorLoadingPositions')} {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="position-change-section">
      <Stack gap={3}>
        <Row className="g-3">
          <Col md={6}>
            <Card>
              <Card.Body className="p-0" style={{ height: '400px' }}>
                <ReactFlowProvider>
                  <FlowContent
                    positions={flowPositions}
                    selectedPositionId={selectedPositionId}
                    onPositionSelect={handlePositionSelect}
                    displayStaffedPositions={displayStaffedPositions}
                    showFullHierarchy={showFullHierarchy}
                    t={t}
                  />
                </ReactFlowProvider>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Stack gap={3}>
              <ControlsPanel
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                displayStaffedPositions={displayStaffedPositions}
                onDisplayStaffedChange={setDisplayStaffedPositions}
                showFullHierarchy={showFullHierarchy}
                onShowFullHierarchyChange={setShowFullHierarchy}
                searchPositions={searchPositions}
                selectedPositionId={selectedPositionId}
                onPositionSelect={handlePositionSelect}
                showSearchResults={showSearchResults}
                onSearchFocus={() => setShowSearchResults(true)}
                onSearchClose={() => setShowSearchResults(false)}
                searchContainerRef={searchContainerRef}
                t={t}
              />

              <DateControls
                effectiveDate={effectiveDate}
                unknownEndDate={unknownEndDate}
                endDate={endDate}
                onEffectiveDateChange={handleEffectiveDateChange}
                onEndDateChange={handleEndDateChange}
                onUnknownEndDateChange={handleUnknownEndDateChange}
                t={t}
              />
            </Stack>
          </Col>
        </Row>

        <PositionSummary
          currentPosition={currentPosition}
          selectedPosition={selectedPosition}
          selectedPositionId={selectedPositionId}
          t={t}
        />
      </Stack>
    </div>
  );
};
